import { PDFPaperSize } from "@/constants/mapStyles";
import PDFFooter from "@components/pdf-partials/PDFFooter";
import PDFHeader from "@components/pdf-partials/PDFHeader";
import { EmeraldIcon } from "@emerald-react/icon";
import { useSurvey } from "@hooks/useSurveySelectors";
import { formatDate } from "@shared/utils";
import { getIconData } from "@shared/utils/map";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import type { Property } from "@survey-types/survey.property.types";
import classNames from "classnames";
import type { TourScheduleFieldVisibility } from "../tour/tourScheduleFieldConfig";
import { useGetTourStopsQuery } from "@services/survey-tour-stops";
import type { TourStop } from "@services/survey-tour-stops";
import {
  getTourPropertyDisplayNumbers,
  resolveTourNumberingMode,
} from "../tour/tourNumbering";

interface TourScheduleSectionPreviewProps {
  mode?: string;
}

// Type for tour schedule items
type TourScheduleItem = 
  | { type: 'property'; property: Property; displayNumber?: number }
  | { type: 'customStop'; stop: TourStop };

const sanitizeAddressPart = (value: unknown): string => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const normalized = text.toLowerCase();
  if (text === "-" || text === "—" || normalized === "n/a" || normalized === "na") {
    return "";
  }

  return text;
};

// Helper function to chunk array into smaller arrays
const chunkArray = (array: TourScheduleItem[], chunkSize: number): TourScheduleItem[][] => {
  const chunks: TourScheduleItem[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// Component to render a single page of properties and custom stops
const TourSchedulePage = ({
  items,
  scheduleDate,
  fieldVisibility,
  showDirections,
  mode,
}: {
  items: TourScheduleItem[];
  scheduleDate: string;
  fieldVisibility: TourScheduleFieldVisibility;
  showDirections: boolean;
  mode?: string;
}) => {
  return (
    <SurveyPDFPage mode={mode}>
      <div className="w-full h-full flex flex-col bg-[#f9f9f9]">
        {/* Header */}
        <PDFHeader title={`${scheduleDate}`} />

        {/* Body list */}
        <div className="flex-1 pb-24 flex flex-col overflow-hidden min-h-0">
          {items.length === 0 ? (
            <p className="text-gray-500 mt-8 text-sm">
              No properties available for the tour schedule.
            </p>
          ) : (
            <div className="flex-1 divide-y divide-gray-200 w-full">
              <ul className="min-h-0 w-full">
                {items.map((item, itemIndex) => {
                  if (item.type === 'customStop') {
                    // Render custom stop at the top
                    const stop = item.stop;
                    const stopDetails = (stop.stopDetails || "").trim();
                    const displayText = stop.label;
                    const addressText = stop.address || (stop.stopType === "CURRENT_LOCATION" ? "Current location" : "Custom location");
                    
                    return (
                      <li
                        key={stop.stopId}
                        className={classNames(
                          "flex items-center justify-between h-15 px-8 w-full leading-2.75",
                          {
                            "bg-white": itemIndex % 2 === 0,
                            "bg-secondary-lightest": itemIndex % 2 !== 0,
                          },
                        )}
                      >
                        <div
                          style={{ padding: "8px 0" }}
                          className="flex items-center"
                        >
                          <div className="shrink-0 mr-6 w-6 h-6">
                            <div className="w-6 h-6 flex items-center justify-center">
                              <EmeraldIcon 
                                icon={stop.stopType === "CURRENT_LOCATION" ? "star" : "location_on"} 
                                type={stop.stopType === "CURRENT_LOCATION" ? "filled" : "outlined"}
                                className={`text-xl ${
                                  stop.stopType === "CURRENT_LOCATION" ? "text-warning-icon" : "text-primary"
                                }`}
                              />
                            </div>
                          </div>
                          <div>
                            <p className="text-sm leading-snug font-medium">{displayText}</p>
                            <p className="text-xs text-gray-500">{addressText}</p>
                            {stopDetails && (
                              <p
                                className="text-xs text-gray-500 mt-0.5 tracking-wide line-clamp-2"
                                title={stopDetails}
                              >
                                {stopDetails}
                              </p>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  } else {
                    // Render property
                    const property = item.property;
                    const currentStopNumber = item.displayNumber;
                    const propertyStopDetails = (property.stopDetails || "").trim();
                    const safeAddress1 = sanitizeAddressPart(property.address1);
                    const propertyWithAddressLine2 = property as Property & {
                      addressLine2?: string;
                      address2?: string;
                    };
                    const safeAddress2 = sanitizeAddressPart(
                      propertyWithAddressLine2.addressLine2 ??
                        propertyWithAddressLine2.address2,
                    );
                    const safeCity = sanitizeAddressPart(property.city);
                    const safeState = sanitizeAddressPart(property.stateProvince);
                    const safeZip = sanitizeAddressPart(property.postalCode);
                    const details = [
                      fieldVisibility.propertyName ? sanitizeAddressPart(property.name) : "",
                      fieldVisibility.streetAddress ? safeAddress1 : "",
                      fieldVisibility.addressLine2 ? safeAddress2 : "",
                      fieldVisibility.city ? safeCity : "",
                      fieldVisibility.state ? safeState : "",
                      fieldVisibility.zipCode ? safeZip : "",
                    ]
                      .filter(Boolean)
                      .join(", ");
                    const hasLatLng = property.latitude && property.longitude;
                    const encodedAddress = encodeURIComponent(
                      `${safeAddress1}${safeCity ? "," + safeCity : ""}${safeState ? "," + safeState : ""}${safeZip ? "," + safeZip : ""}`
                        .replace(/\s+/g, " ")
                        .trim(),
                    );
                    const mapsHref = hasLatLng
                      ? `https://www.google.com/maps/dir//${safeAddress1},${safeCity},+${safeState}+${safeZip}/@${property.latitude},${property.longitude}`
                      : `https://www.google.com/maps/dir//${encodedAddress}`;
                    
                    return (
                      <li
                        key={property.propertyId}
                          className={classNames(
                            "flex items-center justify-between h-15 px-8 w-full leading-2.75",
                            {
                              "bg-white": itemIndex % 2 === 0,
                              "bg-secondary-lightest": itemIndex % 2 !== 0,
                            },
                          )}
                        >
                          <div
                            style={{ padding: "8px 0" }}
                            className="flex items-center"
                          >
                            <div className="shrink-0 mr-6 w-6 h-6">
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-sm"
                                style={{
                                  backgroundColor: getIconData(property).iconColor,
                                }}
                                title={
                                  currentStopNumber
                                    ? `Stop ${currentStopNumber}`
                                    : undefined
                                }
                              >
                                {currentStopNumber ?? "-"}
                              </div>
                            </div>
                            <div>
                              {details && (
                                <p className="text-sm leading-snug">{details}</p>
                              )}
                              {propertyStopDetails && (
                                <p
                                  className="text-xs text-gray-500 mt-0.5 tracking-wide line-clamp-2"
                                  title={propertyStopDetails}
                                >
                                  {propertyStopDetails}
                                </p>
                              )}
                            </div>
                          </div>
                          {showDirections && (
                            <div className="ml-6 flex items-center">
                              <a
                                href={mapsHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open ${property.address1} in Google Maps`}
                              >
                                <EmeraldIcon
                                  icon="directions"
                                  type="outlined"
                                  variant="symbol"
                                  className="text-primary-dark w-6 h-6"
                                />
                              </a>
                            </div>
                          )}
                        </li>
                    );
                  }
                })}
              </ul>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="h-12">
          <PDFFooter />
        </div>
      </div>
    </SurveyPDFPage>
  );
};

// Preview component for the Tour Schedule section
const TourScheduleSectionPreview = ({
  mode,
}: TourScheduleSectionPreviewProps) => {
  const survey = useSurvey();
  const surveyId = survey?.id || "";
  
  // Fetch tour stops
  const { data: tourStops = [] } = useGetTourStopsQuery(surveyId, {
    skip: !surveyId,
  });
  
  const fieldVisibility: TourScheduleFieldVisibility = {
    propertyName: survey?.isIncludeTourPropertyName !== false,
    streetAddress: survey?.isIncludeTourStreetAddress !== false,
    addressLine2: survey?.isIncludeTourAddressLine2 !== false,
    city: survey?.isIncludeTourCity !== false,
    state: survey?.isIncludeTourState !== false,
    zipCode: survey?.isIncludeTourZipCode !== false,
  };
  const showDirections = survey?.isIncludeTourArrow !== false;
  const tourNumberingMode = resolveTourNumberingMode(
    surveyId,
    survey?.tourNumberingMode,
  );

  // Use Redux array order directly. Sorting by .order caused drift when
  // individual property PATCH responses updated a single property's .order
  // while others retained stale optimistic values. Redux array order
  // reflects Manage Properties reorders correctly.
  const properties: Property[] = survey?.properties || [];

  const propertyDisplayNumbers = getTourPropertyDisplayNumbers({
    properties,
    mode: tourNumberingMode,
  });

  // Determine the order threshold to split independent stops
  const lastPropertyOrder = properties.length > 0 
    ? Math.max(...properties.map(p => p.order ?? 0))
    : -1;

  // Get all independent custom stops (not attached to any property)
  const independentStops = tourStops
    .filter((stop) => !stop.propertyId)
    .sort((a, b) => a.order - b.order);

  // Split independent stops into top and bottom groups based on order
  const independentStopsTop = independentStops.filter(
    stop => lastPropertyOrder === -1 || stop.order <= lastPropertyOrder
  );

  const independentStopsBottom = independentStops.filter(
    stop => lastPropertyOrder !== -1 && stop.order > lastPropertyOrder
  );

  // Create tour schedule structure:
  // 1) Independent stops at top (order <= last property order)
  // 2) For each property:
  //    - Render property card ONLY if included in tour AND not hidden AND not current location
  //    - Then render attached custom stops below the property
  // 3) Independent stops at bottom (order > last property order)
  const tourItems: TourScheduleItem[] = [];

  // Add independent stops at top
  independentStopsTop.forEach((stop) => {
    tourItems.push({ type: "customStop", stop });
  });

  // Add property + attached custom stops for each property
  properties.forEach((property) => {
    const attachedStops = tourStops
      .filter((stop) => stop.propertyId === property.propertyId)
      .sort((a, b) => a.order - b.order);

    // Only render property card if it's included in tour schedule AND not hidden AND not current location
    const shouldShowProperty = 
      property.isIncludeTourSchedule !== false &&
      !property.isHidden &&
      !property.isCurrentLocation;
      
    if (shouldShowProperty) {
      tourItems.push({
        type: "property",
        property,
        displayNumber: propertyDisplayNumbers[property.propertyId],
      });
    }

    // Always render attached custom stops BELOW the property (even if property is toggled off/hidden/current location)
    attachedStops.forEach((stop) => {
      tourItems.push({ type: "customStop", stop });
    });
  });

  // Add independent stops at bottom
  independentStopsBottom.forEach((stop) => {
    tourItems.push({ type: "customStop", stop });
  });

  // `tourDetails` currently stores the date text per TourScheduleSection
  const scheduleDate = survey?.tourDetails
    ? survey.tourDetails
    : `Tour Schedule - ${formatDate(new Date(), "dddd MMMM D, YYYY")}`;

  // Determine items per page based on PDF orientation
  const isLandscape = survey?.pdfPaperSize === PDFPaperSize.Landscape;
  const itemsPerPage = isLandscape ? 11 : 15;

  // Split tour items into pages (if empty, create one empty page)
  const itemPages = chunkArray(tourItems, itemsPerPage);
  const pagesToRender = itemPages.length > 0 ? itemPages : [[]];

  // Return all pages
  return (
    <>
      {pagesToRender.map((pageItems, pageIndex) => (
        <TourSchedulePage
          key={`tour-schedule-page-${pageIndex}`}
          items={pageItems}
          scheduleDate={scheduleDate}
          fieldVisibility={fieldVisibility}
          showDirections={showDirections}
          mode={mode}
        />
      ))}
    </>
  );
};

export default TourScheduleSectionPreview;
